import { CYLINDER_FIT_CONFIG, type GeometricCylinderFit } from "./cylinder-fit";
import {
  deserializeCylinder,
  serializeCylinder,
  type CylinderFitter,
  type CylinderFitRequest,
  type CylinderFitResponse,
} from "./cylinder-fitting";

const FIT_TIMEOUT = 10_000;

/** Serial worker owner for one seed and its subsequent Cylinder growth. */
export class CylinderFitWorkerClient {
  private sequence = 0;
  private disposed = false;
  private pending?: {
    id: number;
    resolve(result: GeometricCylinderFit): void;
    reject(error: Error): void;
  };

  /**
   * @param worker Dedicated fitting worker.
   * @param signal Selection/source lifetime.
   */
  constructor(
    private readonly worker: Worker,
    private readonly signal: AbortSignal,
  ) {
    worker.addEventListener("message", this.message);
    worker.addEventListener("error", this.failed);
    worker.addEventListener("messageerror", this.failed);
    signal.addEventListener("abort", this.dispose, { once: true });
    if (signal.aborted) this.dispose();
  }

  private message = (event: MessageEvent<CylinderFitResponse>): void => {
    const { pending } = this;
    if (!pending || pending.id !== event.data.id || this.disposed) return;
    if ("error" in event.data) {
      pending.reject(new Error(event.data.error));
      this.dispose();
    } else {
      pending.resolve({
        ...event.data.result,
        cylinder: deserializeCylinder(event.data.result.cylinder),
      });
    }
  };

  private failed = (): void => {
    this.pending?.reject(new Error("Cylinder fitting worker is unavailable"));
    this.dispose();
  };

  /** @param cancelled Caller generation check, including after asynchronous responses. */
  private assertActive(cancelled: () => boolean): void {
    if (this.disposed || cancelled()) {
      throw new DOMException("Cylinder fitting cancelled", "AbortError");
    }
  }

  /**
   * Fits copied Float64 evidence; shared cloud geometry is never transferred.
   *
   * @param points Bounded world-space evidence, copied for transfer.
   * @param initial Current cylinder, serialized without mutation.
   * @param spacing Source spacing or bounded noise-floor estimate.
   * @param cancelled Selection/source generation check.
   * @param depthPreview Whether support comes from a depth raster.
   * @returns Correlated geometric fit with reconstructed vectors.
   */
  readonly fit: CylinderFitter = async (
    points,
    initial,
    spacing,
    cancelled,
    depthPreview,
  ) => {
    this.assertActive(cancelled);
    if (this.pending) throw new Error("Cylinder fit already in progress");
    if (points.length > CYLINDER_FIT_CONFIG.maximumSamples) {
      throw new Error("Cylinder evidence exceeds worker limit");
    }
    const positions = new Float64Array(points.length * 3);
    points.forEach((point, index) => point.toArray(positions, index * 3));
    const request: CylinderFitRequest = {
      id: ++this.sequence,
      positions,
      initial: serializeCylinder(initial),
      spacing,
      depthPreview,
    };
    const timeout = setTimeout(this.failed, FIT_TIMEOUT);
    try {
      const result = await new Promise<GeometricCylinderFit>(
        (resolve, reject) => {
          this.pending = { id: request.id, resolve, reject };
          this.worker.postMessage(request, [positions.buffer]);
        },
      );
      this.assertActive(cancelled);
      return result;
    } finally {
      clearTimeout(timeout);
      this.pending = undefined;
    }
  };

  /** Terminates synchronous solver work too; no stale response survives disposal. */
  readonly dispose = (): void => {
    if (this.disposed) return;
    this.disposed = true;
    this.pending?.reject(
      new DOMException("Cylinder fitting cancelled", "AbortError"),
    );
    this.worker.removeEventListener("message", this.message);
    this.worker.removeEventListener("error", this.failed);
    this.worker.removeEventListener("messageerror", this.failed);
    this.signal.removeEventListener("abort", this.dispose);
    this.worker.terminate();
  };
}
